/**
 * Password rules, shown live on the account screen.
 *
 * ## These do NOT currently mirror the server, and that is a known divergence
 *
 * ECH-350 says these rules "mirror ECH-109's server-side rules exactly — if one
 * changes, both change". As of this commit they do not. The server
 * (`Echo-Backend/src/validations/custom.validation.js`) enforces only:
 *
 *   at least 8 characters, and at least one letter and one number
 *
 * No uppercase requirement, no symbol requirement. The rules below are the
 * stricter set the ticket and the design specify.
 *
 * Stricter on the client is safe — nothing weak gets through — but it means the
 * mirror claim is false today, and the consequence is real: the mobile app and
 * any direct API caller can still register a password this screen would reject.
 * Closing it is a backend change, tracked as ECH-358, not something this screen
 * can fix. That ticket also records the audit showing sign-in cannot break:
 * neither login schema applies this rule, and neither does the `currentPassword`
 * field on password change.
 *
 * Until then, treat this module as the single client-side definition: every
 * rule is declared once here, and the screen renders whatever this exports.
 */

export const PASSWORD_RULES = [
  {
    id: "length",
    test: (value) => value.length >= 8,
  },
  {
    id: "uppercase",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    id: "number",
    test: (value) => /\d/.test(value),
  },
  {
    id: "symbol",
    // Anything that is not a letter, a digit or whitespace. Defined by
    // exclusion rather than an allowlist so an unusual but legitimate symbol is
    // not silently rejected.
    test: (value) => /[^A-Za-z0-9\s]/.test(value),
  },
];

/** Which rules a candidate password currently satisfies. */
export const evaluatePassword = (value = "") =>
  PASSWORD_RULES.map((rule) => ({ id: rule.id, met: rule.test(value) }));

export const passwordIsValid = (value = "") =>
  PASSWORD_RULES.every((rule) => rule.test(value));

/**
 * Email validation, kept deliberately permissive.
 *
 * The only address that truly matters is one that can receive the verification
 * code, and that is proven by the code arriving — not by a regular expression.
 * Elaborate patterns reject valid addresses (new TLDs, plus-addressing, quoted
 * local parts) far more often than they catch typos, so this checks shape only.
 */
export const emailIsValid = (value = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
